import { defaultSchema } from "rehype-sanitize";

/**
 * The schema type, named explicitly.
 *
 * Without the annotation TypeScript tries to infer a type that reaches into
 * `hast-util-sanitize/lib` — an indirect dependency — and refuses to emit a
 * portable declaration. `rehype-sanitize` re-exports the type for exactly this
 * reason.
 */
type SanitizeSchema = typeof defaultSchema;

/**
 * The sanitization schema (SPEC §13, §59, AGENTS.md §12).
 *
 * Every HTML string this project produces — preview and production alike —
 * passes through this schema. Raw HTML in Markdown is allowed, but only after
 * `rehype-raw` has parsed it and this has stripped everything dangerous.
 *
 * What is deliberately **not** here, and why:
 *
 *   - `script`, `style`, `iframe`, `object`, `embed` — not in `defaultSchema`
 *     and not added. Raw HTML in a Markdown file is author input, and author
 *     input is not trusted.
 *   - `on*` event handlers — `defaultSchema` allows only a fixed attribute
 *     list, so an `onclick` never survives.
 *   - `javascript:` URLs — `defaultSchema.protocols` restricts `href` and `src`
 *     to safe schemes, and this narrows them further.
 *   - `data:` URLs on images — removed below. A `data:` image can carry a
 *     payload that some browsers will sniff.
 *
 * `defaultSchema`'s `clobber` list also prefixes `id` and `name` from raw HTML
 * with `user-content-`, which is what stops DOM clobbering. Heading ids added
 * by `rehype-slug` run *after* this step, so they are unaffected.
 */
export const contentSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,

  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    // Present in the rendered output but absent from the default allow-list.
    "figure",
    "figcaption",
    "details",
    "summary",
    "kbd",
    "mark",
    "abbr",
    // The editor's underline mark serializes to `<u>` — Markdown has no
    // underline marker, and this is the one tag that survives the round trip.
    "u",
  ],

  attributes: {
    ...defaultSchema.attributes,

    // `className` on `*` is already allowed by the default schema and is what
    // makes syntax highlighting work. `id` is not, and is not added: heading
    // ids come from `rehype-slug` after sanitization.
    img: [
      ...(defaultSchema.attributes?.img ?? []),
      "src",
      "alt",
      "title",
      "width",
      "height",
      "loading",
    ],
    a: [...(defaultSchema.attributes?.a ?? []), "href", "title"],
    // GFM task lists render as disabled checkboxes.
    input: ["type", "checked", "disabled"],
    // GFM tables.
    td: [...(defaultSchema.attributes?.td ?? []), "align"],
    th: [...(defaultSchema.attributes?.th ?? []), "align"],
  },

  protocols: {
    ...defaultSchema.protocols,
    // Narrower than the default (which also allows irc, ircs, xmpp).
    href: ["http", "https", "mailto"],
    // No `data:` — see above.
    src: ["http", "https"],
  },
};
